import { createContext, useContext } from 'react'
import type { EnrichedPlaylistItem, EnrichedInProgressItem } from '../../../../../services/community/types'

/**
 * Shared playlist row context.
 *
 * Variants populate this via `<PlaylistRow.Provider value={...}>`.
 * `<PlaylistRow.Header>` reads the name + count + duration + edit-mode
 * fields; `<PlaylistRow.Body>` reads items + hydration + edit-mode.
 *
 * Visibility (PlaylistSharingDropdown state) and per-tab labels are
 * NOT in context — visibility is variant-specific, labels are
 * resolved once per tab and threaded as props to preserve
 * `React.memo` short-circuit semantics on this component.
 */

export type PlaylistRowItem = EnrichedPlaylistItem | EnrichedInProgressItem

export interface PlaylistRowContextValue {
  playlistId: string
  playlistName: string
  playlistType?: string
  items: readonly PlaylistRowItem[]
  /**
   * Shell-reported count. Distinct from `items.length` so the body can
   * paint the right number of skeletons during the hydration window
   * (matches legacy behaviour).
   */
  expectedItemCount: number
  totalDuration: string | null
  primaryTopic?: string
  isHydrating: boolean
  isEditing: boolean
}

export const PlaylistRowContext = createContext<PlaylistRowContextValue | null>(null)

/**
 * Read the context. Throws outside `<PlaylistRow.Provider>` — the
 * compound never auto-creates a default, because a silent default
 * (empty items, count 0) would mask wiring bugs as "row renders but
 * shows empty".
 */
export function useRowContext (): PlaylistRowContextValue {
  const ctx = useContext(PlaylistRowContext)
  if (!ctx) {
    throw new Error('useRowContext must be used inside <PlaylistRow.Provider>')
  }
  return ctx
}
